import React, { useState, useEffect, useRef } from 'react';
import { 
  X, Search, Save, Plus, Trash2, RotateCcw, 
  Package, Truck, User, MapPin, Scan, CheckCircle, AlertCircle
} from 'lucide-react';
import '../styles/CustomerInvoice.css';
import apiClient from '../services/api';
import { useNavigate } from 'react-router-dom';

const Outscan = ({ onClose }) => {
  const navigate = useNavigate();
  const scanInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Outscan header
  const [outscanForm, setOutscanForm] = useState({
    OutscanNo: '',
    OutscanDate: new Date().toISOString().slice(0, 16),
    VehicleID: '',
    DriverID: '',
    DestinationCityID: '',
    RouteID: '',
    EmployeeID: '',
    Notes: '',
    Status: 'Pending'
  });
  
  // Scanned items
  const [scannedItems, setScannedItems] = useState([]);
  const [scanInput, setScanInput] = useState('');
  const [scanningMode, setScanningMode] = useState(true);
  
  // Dropdown data
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [cities, setCities] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [routes, setRoutes] = useState([]);
  
  // Statistics
  const [stats, setStats] = useState({
    totalItems: 0,
    totalWeight: 0,
    totalPackages: 0
  });
  
  // Session
  const session = getSessionData();
  
  function getSessionData() {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session:', error);
      return null;
    }
  }
  
  useEffect(() => {
    loadDropdownData();
    generateOutscanNumber();
  }, []);
  
  useEffect(() => {
    // Auto-focus scan input when in scanning mode
    if (scanningMode && scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [scanningMode, scannedItems]);
  
  useEffect(() => {
    // Calculate statistics
    const totalItems = scannedItems.length;
    const totalWeight = scannedItems.reduce((sum, item) => sum + parseFloat(item.Weight || 0), 0);
    const totalPackages = scannedItems.reduce((sum, item) => sum + parseInt(item.NoOfPkgs || 0), 0);
    
    setStats({ totalItems, totalWeight, totalPackages });
  }, [scannedItems]);
  
  const loadDropdownData = async () => {
    try {
      const [vehRes, driverRes, cityRes, empRes, routeRes] = await Promise.all([
        apiClient.get('/Vehicles.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/Drivers.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/Cities.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/Employees.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/Routes.php').catch(e => ({ data: { success: false } }))
      ]);
      
      if (vehRes.data.success) setVehicles(vehRes.data.data || []);
      if (driverRes.data.success) {
        const activeDrivers = (driverRes.data.data || []).filter(d => d.isactive === 1);
        setDrivers(activeDrivers);
      }
      if (cityRes.data.success) setCities(cityRes.data.data || []);
      if (empRes.data.success) setEmployees(empRes.data.data || []);
      if (routeRes.data.success) setRoutes(routeRes.data.data || []);
      
    } catch (err) {
      console.error('Error loading dropdown data:', err);
    }
  };
  
  const generateOutscanNumber = async () => {
    try {
      const response = await apiClient.get('/Outscan.php/next-number');
      if (response.data.success) {
        setOutscanForm(prev => ({ 
          ...prev, 
          OutscanNo: response.data.data.outscanNo 
        }));
      }
    } catch (err) {
      console.error('Error generating outscan number:', err);
      const timestamp = Date.now();
      setOutscanForm(prev => ({ 
        ...prev, 
        OutscanNo: `OUT-${timestamp}` 
      }));
    }
  };
  
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setOutscanForm(prev => ({ ...prev, [name]: value }));
  };
  
  const handleScanSubmit = async (e) => {
    e.preventDefault();
    
    if (!scanInput.trim()) {
      return;
    }
    
    // Check if already scanned
    if (scannedItems.find(item => item.ConsignmentNo === scanInput.trim())) {
      setError(`Consignment ${scanInput.trim()} already scanned!`);
      setScanInput('');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Fetch consignment details
      const response = await apiClient.get(`/Outscan.php/consignment/${scanInput.trim()}`);
      
      if (response.data.success && response.data.data) {
        const consignment = response.data.data;
        
        // Check if already outscanned
        if (consignment.IsOutscanned === 1 || consignment.OutscanID) {
          setError(`Consignment ${scanInput.trim()} already outscanned on ${new Date(consignment.OutscanDate).toLocaleString()}`);
          setScanInput('');
          setTimeout(() => setError(''), 5000);
          return;
        }
        
        // Add to scanned items
        setScannedItems(prev => [...prev, {
          ConsignmentID: consignment.ConsignmentID,
          ConsignmentNo: consignment.ConsignmentNo,
          ConsignmentDate: consignment.ConsignmentDate,
          ShipperName: consignment.ShipperName,
          ConsigneeName: consignment.ConsigneeName,
          DestinationCity: consignment.DestinationCity,
          Weight: consignment.Weight || 0,
          NoOfPkgs: consignment.NoOfPkgs || 0,
          Status: consignment.Status,
          ScannedAt: new Date().toISOString()
        }]);
        
        setSuccess(`✓ Scanned: ${scanInput.trim()}`);
        setScanInput('');
        setTimeout(() => setSuccess(''), 2000);
        
        // Play success sound (optional)
        playBeep();
        
      } else {
        setError(`Consignment ${scanInput.trim()} not found!`);
        setScanInput('');
        setTimeout(() => setError(''), 3000);
      }
      
    } catch (err) {
      console.error('Error scanning consignment:', err);
      setError('Error scanning: ' + (err.response?.data?.message || err.message));
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };
  
  const playBeep = () => {
    // Simple beep sound using AudioContext
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (err) {
      // Silently fail if audio not supported
    }
  };
  
  const handleRemoveItem = (index) => {
    setScannedItems(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleSaveOutscan = async () => {
    if (scannedItems.length === 0) {
      setError('Please scan at least one consignment');
      return;
    }
    
    if (!outscanForm.VehicleID) {
      setError('Please select a vehicle');
      return;
    }
    
    if (!outscanForm.DriverID) {
      setError('Please select a driver');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const dataToSave = {
        ...outscanForm,
        Items: scannedItems.map(item => ({
          ConsignmentID: item.ConsignmentID,
          ConsignmentNo: item.ConsignmentNo
        })),
        TotalItems: stats.totalItems,
        TotalWeight: stats.totalWeight,
        TotalPackages: stats.totalPackages
      };
      
      const response = await apiClient.post('/Outscan.php', dataToSave);
      
      if (response.data.success) {
        setSuccess(`Outscan ${outscanForm.OutscanNo} saved successfully! ${stats.totalItems} items outscanned.`);
        setTimeout(() => {
          handleClearAll();
        }, 2000);
      } else {
        setError(response.data.message || 'Failed to save outscan');
      }
    } catch (err) {
      console.error('Error saving outscan:', err);
      setError('Error saving outscan: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  const handleClearAll = () => {
    setOutscanForm({
      OutscanNo: '',
      OutscanDate: new Date().toISOString().slice(0, 16),
      VehicleID: '',
      DriverID: '',
      DestinationCityID: '',
      RouteID: '',
      EmployeeID: '',
      Notes: '',
      Status: 'Pending'
    });
    setScannedItems([]);
    setScanInput('');
    setError('');
    setSuccess('');
    generateOutscanNumber();
  };
  
  if (!session?.companyid) {
    return (
      <div 
        className="invoice-form-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            if (onClose) {
              onClose();
            } else {
              navigate('/dashboard');
            }
          }
        }}
      >
        <div className="invoice-form-container" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
          <div className="form-header">
            <h2>Session Required</h2>
            <button 
              className="close-btn-fixed" 
              onClick={() => {
                if (onClose) {
                  onClose();
                } else {
                  navigate('/dashboard');
                }
              }}
              type="button"
              title="Close"
            >
              <X size={20} />
            </button>
          </div>
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <p>Please select a company and financial year from the dashboard.</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className="invoice-form-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          if (onClose) {
            onClose();
          } else {
            navigate('/dashboard');
          }
        }
      }}
    >
      <div className="invoice-form-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="form-header">
          <div className="header-left">
            <Scan size={24} />
            <h2>Outscan - Dispatch Scanning</h2>
          </div>
          <button 
            className="close-btn-fixed"
            onClick={() => {
              if (onClose) {
                onClose();
              } else {
                navigate('/dashboard');
              }
            }}
            type="button"
            title="Close (ESC)"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Toolbar */}
        <div className="invoice-toolbar">
          <button 
            onClick={handleSaveOutscan} 
            disabled={loading || scannedItems.length === 0} 
            className="btn-toolbar btn-save"
          >
            <Save size={18} />
            Save Outscan ({stats.totalItems} items)
          </button>
          <button onClick={handleClearAll} disabled={loading} className="btn-toolbar">
            <RotateCcw size={18} />
            Clear All
          </button>
          <button 
            onClick={() => setScanningMode(!scanningMode)} 
            className="btn-toolbar"
            style={{ 
              background: scanningMode ? '#10b981' : '#6b7280',
              color: 'white'
            }}
          >
            <Scan size={18} />
            {scanningMode ? 'Scanning Active' : 'Scanning Paused'}
          </button>
        </div>
        
        {/* Alerts */}
        {error && (
          <div className="alert alert-error">
            <AlertCircle size={20} />
            <strong>Error:</strong> {error}
          </div>
        )}
        
        {success && (
          <div className="alert alert-success">
            <CheckCircle size={20} />
            <strong>Success:</strong> {success}
          </div>
        )}
        
        <div className="form-content">
          {/* Outscan Header */}
          <div className="form-section">
            <h3 className="section-title">
              <Package size={18} />
              Outscan Details
            </h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Outscan No *</label>
                <input
                  type="text"
                  name="OutscanNo"
                  value={outscanForm.OutscanNo}
                  readOnly
                  style={{ background: '#e3f2fd', fontWeight: 'bold' }}
                />
              </div>
              
              <div className="form-group">
                <label>Outscan Date & Time *</label>
                <input
                  type="datetime-local"
                  name="OutscanDate"
                  value={outscanForm.OutscanDate}
                  onChange={handleFormChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Vehicle *</label>
                <select
                  name="VehicleID"
                  value={outscanForm.VehicleID}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">Select Vehicle</option>
                  {vehicles.map(veh => (
                    <option key={veh.VehicleID} value={veh.VehicleID}>
                      {veh.RegistrationNo} {veh.VehicleType && `- ${veh.VehicleType}`}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Driver *</label>
                <select
                  name="DriverID"
                  value={outscanForm.DriverID}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">Select Driver</option>
                  {drivers.map(driver => (
                    <option key={driver.driverid} value={driver.driverid}>
                      {driver.drivername}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Destination City</label>
                <select
                  name="DestinationCityID"
                  value={outscanForm.DestinationCityID}
                  onChange={handleFormChange}
                >
                  <option value="">Select Destination</option>
                  {cities.map(city => (
                    <option key={city.cityid} value={city.cityid}>
                      {city.cityname}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Route</label>
                <select
                  name="RouteID"
                  value={outscanForm.RouteID}
                  onChange={handleFormChange}
                >
                  <option value="">Select Route</option>
                  {routes.map(route => (
                    <option key={route.routeid} value={route.routeid}>
                      {route.routename}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Scanned By (Employee)</label>
                <select
                  name="EmployeeID"
                  value={outscanForm.EmployeeID}
                  onChange={handleFormChange}
                >
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.employeeid} value={emp.employeeid}>
                      {emp.employeename}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  name="Status"
                  value={outscanForm.Status}
                  onChange={handleFormChange}
                >
                  <option value="Pending">Pending</option>
                  <option value="Dispatched">Dispatched</option>
                  <option value="In Transit">In Transit</option>
                </select>
              </div>
              
              <div className="form-group full-width">
                <label>Notes</label>
                <textarea
                  name="Notes"
                  value={outscanForm.Notes}
                  onChange={handleFormChange}
                  rows="2"
                  placeholder="Additional notes"
                />
              </div>
            </div>
          </div>
          
          {/* Scanner Section */}
          <div className="form-section" style={{ 
            background: scanningMode ? '#f0fdf4' : '#f9fafb',
            border: `2px solid ${scanningMode ? '#10b981' : '#e5e7eb'}`
          }}>
            <h3 className="section-title" style={{ 
              background: scanningMode ? '#dcfce7' : '#f3f4f6',
              padding: '8px 12px',
              borderRadius: '6px'
            }}>
              <Scan size={18} />
              Scan Consignments
              {scanningMode && (
                <span style={{ 
                  marginLeft: 'auto', 
                  fontSize: '14px', 
                  color: '#10b981',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span className="pulse-dot"></span>
                  READY TO SCAN
                </span>
              )}
            </h3>
            
            <form onSubmit={handleScanSubmit} style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                <div className="form-group" style={{ flex: 1, margin: 0 }}>
                  <label style={{ fontWeight: 'bold', fontSize: '16px' }}>
                    Consignment Number / Barcode
                  </label>
                  <input
                    ref={scanInputRef}
                    type="text"
                    value={scanInput}
                    onChange={(e) => setScanInput(e.target.value)}
                    placeholder="Scan or enter consignment number..."
                    disabled={!scanningMode || loading}
                    autoFocus
                    style={{ 
                      fontSize: '18px',
                      padding: '12px',
                      background: scanningMode ? 'white' : '#f3f4f6',
                      border: `2px solid ${scanningMode ? '#10b981' : '#d1d5db'}`
                    }}
                  />
                </div>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={!scanningMode || loading || !scanInput.trim()}
                  style={{ 
                    padding: '12px 24px',
                    height: '48px'
                  }}
                >
                  <Plus size={20} />
                  Scan
                </button>
              </div>
            </form>
          </div>
          
          {/* Statistics Cards */}
          {scannedItems.length > 0 && (
            <div className="form-section">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                <div style={{ background: '#eff6ff', padding: '16px', borderRadius: '8px', border: '1px solid #dbeafe' }}>
                  <div style={{ fontSize: '13px', color: '#1e40af', fontWeight: '600', marginBottom: '4px' }}>
                    Total Items Scanned
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1e3a8a' }}>
                    {stats.totalItems}
                  </div>
                </div>
                
                <div style={{ background: '#f0fdf4', padding: '16px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                  <div style={{ fontSize: '13px', color: '#15803d', fontWeight: '600', marginBottom: '4px' }}>
                    Total Weight (kg)
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#166534' }}>
                    {stats.totalWeight.toFixed(2)}
                  </div>
                </div>
                
                <div style={{ background: '#fef3c7', padding: '16px', borderRadius: '8px', border: '1px solid #fde68a' }}>
                  <div style={{ fontSize: '13px', color: '#92400e', fontWeight: '600', marginBottom: '4px' }}>
                    Total Packages
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#78350f' }}>
                    {stats.totalPackages}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Scanned Items List */}
          <div className="form-section">
            <h3 className="section-title">
              <Package size={18} />
              Scanned Items ({scannedItems.length})
            </h3>
            
            {scannedItems.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px', 
                color: '#6b7280',
                background: '#f9fafb',
                borderRadius: '8px'
              }}>
                <Scan size={48} style={{ opacity: 0.3, margin: '0 auto 16px' }} />
                <p>No items scanned yet. Start scanning consignments above.</p>
              </div>
            ) : (
              <div className="invoice-items-table">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>#</th>
                      <th>Consignment No</th>
                      <th>Date</th>
                      <th>Shipper</th>
                      <th>Consignee</th>
                      <th>Destination</th>
                      <th className="text-right">Weight</th>
                      <th className="text-right">Pkgs</th>
                      <th>Scanned At</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scannedItems.map((item, index) => (
                      <tr key={index} style={{ background: index % 2 === 0 ? 'white' : '#f9fafb' }}>
                        <td style={{ fontWeight: 'bold', color: '#667eea' }}>{index + 1}</td>
                        <td style={{ fontFamily: 'monospace', fontWeight: '600' }}>
                          {item.ConsignmentNo}
                        </td>
                        <td style={{ fontSize: '13px' }}>
                          {new Date(item.ConsignmentDate).toLocaleDateString()}
                        </td>
                        <td style={{ fontSize: '13px' }}>{item.ShipperName || '-'}</td>
                        <td style={{ fontSize: '13px' }}>{item.ConsigneeName || '-'}</td>
                        <td style={{ fontSize: '13px' }}>{item.DestinationCity || '-'}</td>
                        <td className="text-right" style={{ fontWeight: '600' }}>
                          {parseFloat(item.Weight).toFixed(2)}
                        </td>
                        <td className="text-right">{item.NoOfPkgs}</td>
                        <td style={{ fontSize: '12px', color: '#6b7280' }}>
                          {new Date(item.ScannedAt).toLocaleTimeString()}
                        </td>
                        <td className="actions-cell">
                          <button
                            onClick={() => handleRemoveItem(index)}
                            className="btn-icon btn-delete"
                            title="Remove"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#f3f4f6', fontWeight: 'bold' }}>
                      <td colSpan="6" style={{ textAlign: 'right', paddingRight: '12px' }}>
                        Totals:
                      </td>
                      <td className="text-right" style={{ color: '#667eea', fontSize: '16px' }}>
                        {stats.totalWeight.toFixed(2)}
                      </td>
                      <td className="text-right" style={{ color: '#667eea', fontSize: '16px' }}>
                        {stats.totalPackages}
                      </td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Pulse animation for scanning indicator */}
      <style jsx>{`
        .pulse-dot {
          display: inline-block;
          width: 8px;
          height: 8px;
          background: #10b981;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.2);
          }
        }
        
        .alert {
          display: flex;
          align-items: center;
          gap: 12px;
        }
      `}</style>
    </div>
  );
};

export default Outscan;